import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { portalApi } from '@/lib/api/portal.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import { PortalProgress } from '@/components/portal/PortalProgress.js';
import { cn } from '@/lib/utils.js';

const filters = [
  { id: 'active', label: 'Active' },
  { id: 'completed', label: 'Completed' },
  { id: 'all', label: 'All' },
] as const;

export function PortalTicketsPage() {
  const [filter, setFilter] = useState<(typeof filters)[number]['id']>('active');
  const ticketsQuery = useQuery({
    queryKey: ['portal', 'tickets', filter],
    queryFn: () => portalApi.tickets({ status: filter, pageSize: 50 }),
  });

  const tickets = ticketsQuery.data?.data ?? [];

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold text-slate-950">My Repairs</h1>

      <div className="flex gap-2">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={cn(
              'rounded-full px-4 py-1.5 text-sm font-semibold',
              filter === f.id
                ? 'bg-[#1B4F8A] text-white'
                : 'bg-white text-slate-600 ring-1 ring-slate-200',
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {tickets.map((t) => (
          <div key={t.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-bold text-slate-950">{t.ticketNumber}</div>
                <div className="text-sm text-slate-500">{formatDateTime(t.createdAt)}</div>
              </div>
            </div>
            <div className="mt-2 font-semibold text-slate-800">{t.deviceSummary}</div>
            <div className="text-sm text-slate-500">{t.repairSummary}</div>
            <div className="mt-3">
              <PortalProgress index={t.progressIndex} />
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-sm">
              <div className="text-slate-600">
                {t.promisedDate ? <>Est. Ready: {formatDateTime(t.promisedDate)}</> : null}
                {Number(t.balanceDue) > 0 ? (
                  <div>Balance Due: {formatCurrency(t.balanceDue)}</div>
                ) : null}
              </div>
              <Link to={`/portal/tickets/${t.id}`} className="font-semibold text-[#1B4F8A]">
                View Details →
              </Link>
            </div>
          </div>
        ))}
        {tickets.length === 0 && !ticketsQuery.isLoading ? (
          <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-slate-500">
            No repairs found.
          </p>
        ) : null}
      </div>
    </div>
  );
}
